/**
 * Compatibility shim: react-query v3 API on top of @tanstack/react-query v5.
 *
 * The ported tree calls the v3 positional signatures and relies on v3 behaviours
 * that v5 removed:
 *   - useQuery([key], fn, options)            -> useQuery({ queryKey, queryFn, ...})
 *   - useQuery({ ...v5 object })              -> passed through
 *   - useMutation(fn, options)                -> useMutation({ mutationFn, ...})
 *   - onSuccess / onError / onSettled on useQuery (removed in v5) -> emulated
 *   - cacheTime -> gcTime, keepPreviousData -> placeholderData
 *
 * Rather than rewrite ~169 call sites, they import from here.
 */
import { useEffect, useRef } from 'react';
import {
  useQuery as useQueryBase,
  useMutation as useMutationBase,
  keepPreviousData,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

export {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
  QueryCache,
  MutationCache,
  keepPreviousData,
} from '@tanstack/react-query';

type AnyObj = Record<string, any>;

const isPlainObject = (x: unknown): x is AnyObj =>
  !!x && typeof x === 'object' && !Array.isArray(x);

/** Normalise v3-era option keys to their v5 equivalents. */
function normaliseOptions(options: AnyObj): AnyObj {
  const opts = { ...options };
  if ('cacheTime' in opts) {
    opts.gcTime = opts.cacheTime;
    delete opts.cacheTime;
  }
  if (opts.keepPreviousData) {
    opts.placeholderData = keepPreviousData;
    delete opts.keepPreviousData;
  }
  return opts;
}

/** Fire v3 onSuccess/onError/onSettled callbacks that v5 dropped from useQuery. */
function useQueryCallbacks(
  result: AnyObj,
  callbacks: { onSuccess?: (d: any) => void; onError?: (e: any) => void; onSettled?: (d: any, e: any) => void }
) {
  const cb = useRef(callbacks);
  cb.current = callbacks;
  const lastData = useRef<unknown>(undefined);
  const lastError = useRef<unknown>(undefined);

  useEffect(() => {
    if (result.isSuccess && result.data !== lastData.current) {
      lastData.current = result.data;
      cb.current.onSuccess?.(result.data);
      cb.current.onSettled?.(result.data, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.isSuccess, result.data]);

  useEffect(() => {
    if (result.isError && result.error !== lastError.current) {
      lastError.current = result.error;
      cb.current.onError?.(result.error);
      cb.current.onSettled?.(undefined, result.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.isError, result.error]);
}

// Typed overload for the v3 positional form `useQuery([key], fn, opts?)`:
// infer the data type from the query function so consumers get a typed `data`.
// (The axios instance is retyped to resolve to the payload, so a service call's
// return type is the DTO — this threads it through to the component.)
export function useQuery<TQueryFnData>(
  queryKey: readonly unknown[],
  queryFn: () => TQueryFnData | Promise<TQueryFnData>,
  options?: AnyObj
): UseQueryResult<Awaited<TQueryFnData>>;
// Catch-all for the v5 object form and the `useQuery(key, options)` form.
export function useQuery(arg1: any, arg2?: any, arg3?: any): any;
export function useQuery(arg1: any, arg2?: any, arg3?: any): any {
  let options: AnyObj;

  if (isPlainObject(arg1) && arg2 === undefined && arg3 === undefined && ('queryKey' in arg1 || 'queryFn' in arg1)) {
    // Already a v5-style options object.
    options = { ...arg1 };
  } else {
    const queryKey = Array.isArray(arg1) ? arg1 : [arg1];
    if (typeof arg2 === 'function') {
      options = { queryKey, queryFn: arg2, ...(arg3 ?? {}) };
    } else {
      options = { queryKey, ...(arg2 ?? {}) };
    }
  }

  const { onSuccess, onError, onSettled, ...rest } = normaliseOptions(options);
  const result = useQueryBase(rest as UseQueryOptions);
  useQueryCallbacks(result, { onSuccess, onError, onSettled });

  // v3 compatibility: expose `status === 'loading'` (v5 renamed it to 'pending').
  if ((result as AnyObj).status === 'pending') {
    return new Proxy(result as AnyObj, {
      get(target, prop) {
        if (prop === 'status') return 'loading';
        return target[prop as keyof typeof target];
      },
    });
  }
  return result;
}

export function useMutation(arg1: any, arg2?: any): any {
  let options: AnyObj;
  if (typeof arg1 === 'function') {
    options = { mutationFn: arg1, ...(arg2 ?? {}) };
  } else {
    options = { ...(arg1 ?? {}) };
  }
  return useMutationBase(normaliseOptions(options));
}
